import { createFileRoute } from "@tanstack/react-router";
import { GrowthPlanSections } from "@/components/GrowthPlanSections";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    "q": "How do med spas get more bookings?",
    "a": "Treatment-specific pages plus Map Pack dominance plus review velocity. Shoppers search by treatment ('Botox', 'lip filler', 'CoolSculpting'). Pages that rank for each treatment win the booking."
  },
  {
    "q": "Is Google Ads worth it for med spas?",
    "a": "Yes — body contouring, laser, and high-ticket injectables justify CPCs. We build campaigns with first-treatment offers and intake screening."
  },
  {
    "q": "How important are reviews?",
    "a": "Decisive — Map Pack rank and booking rate both follow review count and recency. We integrate review workflows into your post-treatment process."
  },
  {
    "q": "How do local med spas compete with national chains?",
    "a": "Personal connection, provider credibility, and review velocity. Chains polish; locals build relationships. The right marketing makes that relationship visible online."
  },
  {
    "q": "Do you market new openings and grand-launch promotions?",
    "a": "Yes — grand-opening campaigns are a specialty. We launch with paid social, Google ads, and a launch-focused site that books out the first 60 days."
  }
];

export const Route = createFileRoute("/med-spa-marketing-colorado")({
  head: () =>
    industryMarketingHead("Med Spa", "med spa", "med-spa-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry={"Med Spa"}
      industryLower={"med spa"}
      slug={"med-spa-marketing-colorado"}
      subheadline={"We help Colorado med spas drive bookings for Botox, filler, laser, and body contouring — without depending on Instagram alone."}
      painPoints={["Instagram followers don't translate into booked appointments","Larger national med spas outrank you on every service","Your reviews aren't growing fast enough to win the Map Pack"]}
      serviceDescriptions={{
        seo: "Rank for Botox, filler, laser, microneedling, body contouring, and every treatment across every Colorado market.",
        googleMaps: "Own 'med spa near me' in the Map Pack with review velocity that converts.",
        webDesign: "Treatment-specific pages — Botox, filler, CoolSculpting, IPL — with before/afters and online booking.",
        ppc: "High-margin Google Ads for body contouring, laser, and injectable searches with strong ROAS.",
        geoAi: "Get cited when shoppers ask ChatGPT 'best med spa in Denver' or 'Botox near me'.",
        socialMedia: "Provider-led content that builds trust and converts followers into booked treatments.",
      }}
      testimonial={{"quote":"Booking calendar fills a month out. The treatment pages and Google ads completely replaced our Instagram dependence.","role":"Med Spa Owner & RN Injector","city":"Denver"}}
      faqs={FAQS}
      extraSections={<GrowthPlanSections
          quoteIndustry="Med Spa"
          eyebrow="Patient acquisition"
          tipsHeading="How Colorado med spas book more treatments"
          tips={[
            { title: "Show provider credentials", body: "Name your injectors and their training. Clients book with people they trust." },
            { title: "One page per treatment", body: "Botox, filler, laser, microneedling and body contouring each need their own page to show up in search." },
            { title: "Book in two taps", body: "A visible call button and short booking form on phones turns searches into appointments." },
            { title: "Reviews after every visit", body: "A steady flow of fresh Google reviews keeps you in the top three on Google Maps." },
            { title: "Real photos of your team", body: "Clients choose a provider they feel they know. Skip stock photos." },
            { title: "Clear answers for AI search", body: "Short, honest answers to common questions help ChatGPT and Google name your med spa." },
          ]}
          planHeading="Your 90-day local SEO plan for a Colorado med spa"
          plan={[
            { label: "Days 1–30", title: "Fix the foundation", items: ["Google Business Profile categories, hours, photos and services", "Fast, mobile-friendly site with pricing guidance and booking up top", "Name, address and phone matched across directories"] },
            { label: "Days 31–60", title: "Build treatment and town pages", items: ["Pages for each treatment, with real before-and-after photos where allowed", "Pages for the nearby towns you draw clients from", "Client questions answered in plain words"] },
            { label: "Days 61–90", title: "Grow trust and visibility", items: ["Review requests tied to completed visits", "Weekly Google Business Profile posts", "Track calls and bookings, then double down on what works"] },
          ]}
          caseStudySlugs={["dental-woodland-park", "landscaping-castle-rock"]}
          caseStudyNote="We do not have a published med spa case study yet. These are the closest real results: a Colorado health practice and a Castle Rock local business."
          links={[
            { to: "/learn/tips/med-spa-marketing-tips-colorado", label: "Med spa marketing tips for Colorado" },
            { to: "/med-spa-web-design-castle-rock", label: "Med spa web design in Castle Rock" },
            { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
            { to: "/pricing", label: "Pricing" },
          ]}
        />}
      cityHubLinks={[{"to":"/colorado-springs","label":"Med spa marketing in Colorado Springs","description":"Compete with the major Colorado Springs med spas across the Pikes Peak region."},{"to":"/denver-digital-marketing","label":"Med spa marketing in Denver","description":"Win bookings in Denver's saturated injectable and aesthetic market."},{"to":"/boulder-digital-marketing","label":"Med spa marketing in Boulder","description":"Premium aesthetic and wellness for Boulder's beauty-conscious base."}]}
    />
  ),
});
